import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './AddProductModal.css';

export interface ConflictResolutionModalProps {
  isOpen: boolean;
  jiraKey: string;
  existingProductName: string;
  onUseExisting: () => void;
  onDeleteAndRecreate: () => void;
  onCancel: () => void;
}

export function ConflictResolutionModal({
  isOpen,
  jiraKey,
  existingProductName,
  onUseExisting,
  onDeleteAndRecreate,
  onCancel,
}: ConflictResolutionModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;
  if (typeof document === 'undefined' || !document.body) return null;

  return createPortal(
    <div
      className="add-product-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="conflict-resolution-title"
      style={{ zIndex: 'calc(var(--z-modal, 1000) + 220)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="add-product-modal-content conflict-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="add-product-modal-header conflict-modal__header">
          <h2 id="conflict-resolution-title">Jira project key already in use</h2>
          <button
            type="button"
            className="add-product-modal-close conflict-modal__close"
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        <div className="add-product-modal-body conflict-modal__body">
          <p className="conflict-modal__lead">
            The Jira project key <strong>{jiraKey}</strong> is already tracked by{' '}
            <strong>{existingProductName}</strong> in this organization.
          </p>
          <p className="conflict-modal__sublead">Choose what you want to do.</p>
        </div>
        <div className="add-product-modal-footer conflict-modal__footer">
          <div className="conflict-modal__choice">
            <button
              type="button"
              className="add-product-modal-btn-primary conflict-modal__btn"
              onClick={onUseExisting}
            >
              Open existing product
            </button>
            <p className="conflict-modal__hint">
              Switches to <strong>{existingProductName}</strong>. Nothing is created, deleted, or
              moved — the Jira key stays with the existing product.
            </p>
          </div>
          <div className="conflict-modal__choice">
            <button
              type="button"
              className="add-product-modal-btn-danger conflict-modal__btn"
              onClick={onDeleteAndRecreate}
            >
              Hard delete and recreate
            </button>
            <p className="conflict-modal__hint conflict-modal__hint--warn">
              Permanently removes all data for <strong>{existingProductName}</strong> tied to this
              app (worklog, issues, releases, commitments, team, calendars, roles, and settings) and
              recreates it under the new name with Jira key <strong>{jiraKey}</strong>. Audit
              history for the organization is kept. This cannot be undone and requires your account
              password.
            </p>
          </div>
          <button
            type="button"
            className="add-product-modal-btn-secondary conflict-modal__btn conflict-modal__btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
